import moment from 'moment';
import { CountryCode, SignerType } from '~/enums';
import { formatPersonalNumber } from '~/helpers/formatters.helper';
import { TCardOfferDTO } from '~/types/DTO/cardOffer';
import { v4 as uuidv4 } from 'uuid';
import { personsAreTheSame } from '~/helpers/shared.helper';
import { IBeneficialOwner, ISigner } from '~/interfaces/shared';
import {
  ICardOfferApplicant,
  ICardOfferForm,
  ICardOfferOverview,
} from '~/interfaces/cardOffer';

const beneficialOwnersToDTO = (
  owners: IBeneficialOwner[],
  market: CountryCode
): TCardOfferDTO['postOfferReqBeneficialOwner'][] =>
  owners?.map((owner): TCardOfferDTO['postOfferReqBeneficialOwner'] => ({
    id: owner.id,
    isAlternativeOwner: owner.isAlternativeBeneficialOwner || false,
    ownership: owner.percentage || '0',
    fullName: owner.fullName,
    ...(!!owner.personalNumber && {
      personalNumber: formatPersonalNumber(owner.personalNumber, market),
    }),
    ...(!!owner.dateOfBirth && {
      dateOfBirth: moment(owner.dateOfBirth).format('YYYY-MM-DD'),
    }),
    address: {
      zipCode: owner.address?.zipCode || null,
      street: owner.address?.streetName || null,
      city: owner.address?.city || null,
    },
    ownerShare: owner.percentage || '0',
    politicallyExposed: owner.isPEP || false,
    residenceCountryIsoCode: owner.countryOfResidence || '',
    taxResidenceCountryIsoCode: owner.countryOfTaxResidence || '',
    residenceCountry: owner.countryOfResidence || '',
  }));

const signersToDTO = (
  signers: ISigner[]
): TCardOfferDTO['postOfferReqSigner'][] =>
  signers?.map((signer): TCardOfferDTO['postOfferReqSigner'] => ({
    name: signer.name,
    firstName: signer.firstName,
    lastName: signer.lastName,
    email: signer.email || '',
    phoneNumber: signer.phone || '',
    signerId: signer.signerId,
    type: signer.type,
    residenceCountryIsoCode: signer.countryOfResidence || '',
    taxResidenceCountryIsoCode: signer.countryOfTaxResidence || '',
    politicallyExposed: signer.isPEP || false,
  }));

const offerBeneficialOwnersFromDTO = (
  owners: TCardOfferDTO['getOfferRes']['beneficialOwners']
): IBeneficialOwner[] =>
  owners?.map(
    (owner): IBeneficialOwner => {
      const dateOfBirthOnlyContainsYear =
        owner.dateOfBirth?.toString().length === 4;

      return {
        fullName: owner.fullName || '',
        personalNumber: owner?.personalNumber,
        dateOfBirth:
          owner.dateOfBirth && !dateOfBirthOnlyContainsYear
            ? new Date(owner.dateOfBirth)
            : null,
        yearOfBirth: dateOfBirthOnlyContainsYear ? owner.dateOfBirth : '',
        percentage: owner.ownership?.replace(',', '.') || '', // In case API returns comma instead of period
        id: uuidv4(),
        countryOfResidence: owner.residenceCountry,
      };
    }
  );

const offerSignersFromDTO = (
  signers: TCardOfferDTO['getOfferRes']['additionalSigners']
): ISigner[] =>
  signers?.map(
    (signer): ISigner => ({
      name: `${signer.firstName} ${signer.lastName}`,
      firstName: signer.firstName,
      lastName: signer.lastName,
      signerId: signer.signerId,
      email: signer.email,
      phone: signer.phoneNumber,
      type: SignerType.Signer,
    })
  );

const offerApplicantFromDTO = (
  applicant: TCardOfferDTO['getOfferRes']['applicant']
): ICardOfferApplicant => {
  const dateOfBirthOnlyContainsYear =
    applicant.dateOfBirth?.toString().length === 4;

  return {
    fullName: `${applicant.firstName} ${applicant.lastName}`,
    personalNumber: applicant?.personalNumber,
    dateOfBirth:
      applicant.dateOfBirth && !dateOfBirthOnlyContainsYear
        ? new Date(applicant.dateOfBirth)
        : null,
    yearOfBirth: dateOfBirthOnlyContainsYear ? applicant.dateOfBirth : '',
    percentage: '',
    id: uuidv4(),
    signerUuid: applicant.signerUuid,
    signerId: applicant.signerId,
    email: applicant.email,
    phoneNumber: applicant.phoneNumber,
    firstName: applicant.firstName,
    lastName: applicant.lastName,
  };
};

const offerFromDTO = (
  cardOffer: TCardOfferDTO['getOfferRes']
): ICardOfferOverview => ({
  additionalSigners: offerSignersFromDTO(cardOffer.additionalSigners) || [],
  applicant: offerApplicantFromDTO(cardOffer.applicant),
  applicationReasons: cardOffer.applicationReasons || [],
  applicationUuid: cardOffer.applicationUuid || '',
  beneficialOwners:
    offerBeneficialOwnersFromDTO(cardOffer.beneficialOwners) || [],
  clientId: cardOffer.clientId,
  companyName: cardOffer.companyName || '',
  companyType: cardOffer.companyType || '',
  hasAcceptedTerms: false,
  offeredCreditLimit: cardOffer.offeredCreditLimit || 0,
});

const applicantToDTO = (
  applicant: ICardOfferApplicant,
  market: CountryCode
): TCardOfferDTO['postOfferReq']['applicant'] => ({
  dateOfBirth: applicant.dateOfBirth
    ? moment(applicant.dateOfBirth).format('YYYY-MM-DD')
    : null,
  email: applicant.email,
  signerUuid: applicant.signerUuid,
  firstName: applicant.firstName,
  lastName: applicant.lastName,
  ...(!!applicant.personalNumber && {
    personalNumber: formatPersonalNumber(applicant.personalNumber, market),
  }),
  phoneNumber: applicant.phoneNumber,
  signerId: applicant.signerId,
  residenceCountryIsoCode: applicant.countryOfResidence || '',
  taxResidenceCountryIsoCode: applicant.countryOfTaxResidence || '',
  politicallyExposed: applicant.isPEP || false,
  fullName: applicant.fullName || '',
  id: applicant.id || '',
  personalNumber: applicant.personalNumber || '',
  ownership: applicant.percentage || '0',
});

const applicantToBeneficialOwnerDTO = (
  applicant: ICardOfferApplicant,
  market: CountryCode
): TCardOfferDTO['postOfferReqBeneficialOwner'] => ({
  id: applicant.id,
  isAlternativeOwner: applicant.isAlternativeBeneficialOwner || false,
  ownership: applicant.percentage || '0',
  fullName: applicant.fullName,
  ...(!!applicant.personalNumber && {
    personalNumber: formatPersonalNumber(applicant.personalNumber, market),
  }),
  ...(!!applicant.dateOfBirth && {
    dateOfBirth: moment(applicant.dateOfBirth).format('YYYY-MM-DD'),
  }),
  address: {
    zipCode: applicant.address?.zipCode || null,
    street: applicant.address?.streetName || null,
    city: applicant.address?.city || null,
  },
  ownerShare: applicant.percentage || '0',
  politicallyExposed: applicant.isPEP || false,
  residenceCountryIsoCode: applicant.countryOfResidence || '',
  taxResidenceCountryIsoCode: applicant.countryOfTaxResidence || '',
  residenceCountry: applicant.countryOfResidence || '',
});

const formToPostOfferDTO = (
  details: ICardOfferForm,
  market: CountryCode
): TCardOfferDTO['postOfferReq'] => {
  const additionalOwners = details.owners.filter(
    (owner) => !personsAreTheSame(owner, details.applicant)
  );
  return {
    applicant: applicantToDTO(details.applicant, market),
    additionalSigners: signersToDTO(details.signers),
    beneficialOwners: [
      applicantToBeneficialOwnerDTO(details.applicant, market),
      ...beneficialOwnersToDTO(additionalOwners, market),
    ],
    hasAcceptedTerms: true,
    applicationReasons: details.applicationReasons,
  };
};

export const cardOfferDTOMappers = {
  beneficialOwnersToDTO,
  signersToDTO,
  applicantToDTO,
  applicantToBeneficialOwnerDTO,
  formToPostOfferDTO,
  offerFromDTO,
};
